import { useEffect, useRef, useState } from 'react';
import { InputField, PinCodeField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { API_BASE_URL } from '@/config';
import { ClipLoader } from 'react-spinners';

// スキーマ: oldEmail は表示のみなので任意、email と code は検証
const schema = z.object({
  oldEmail: z.string().email().optional(),
  email: z.string().min(1, { message: 'メールアドレスは必須です' })
           .email({ message: 'メールアドレス形式が不正です' }),
  code: z.string().regex(/^\d{6}$/, { message: '6桁の認証コードを入力してください' }),
});

export type LoginFormData = z.infer<typeof schema>;

const LoginForm = ({ onComplete }: { onComplete: () => void }) => {
  const {
    register, watch, getValues, setValue, reset, trigger,
    formState: { errors, isValid }
  } = useForm<LoginFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { oldEmail: '' },
  });

  const intervalTime = 30;
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(intervalTime);
  const refVerifyContainer = useRef<HTMLDivElement>(null);

  // 旧メールを自動取得して表示
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE_URL}/v1/user/info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setValue('oldEmail', d?.email ?? ''))
      .catch(err => console.error('load user info failed:', err));
  }, [setValue]);

  useEffect(() => {
    if (isVerified) {
      refVerifyContainer.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCountdownTimer(intervalTime);
      const timer = setInterval(() => {
        setCountdownTimer(prev => (prev <= 0 ? (clearInterval(timer), 0) : prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isVerified]);

  // 新メールの妥当性チェックを逐次走らせる（ボタン活性制御用）
  const newEmail = watch('email');
  useEffect(() => { if (newEmail !== undefined) trigger('email'); }, [newEmail, trigger]);

  const oldEmail = watch('oldEmail');
  const canSendChange =
    !!newEmail && !errors.email && newEmail !== oldEmail; // 入力あり＆形式OK＆旧と異なる

  // 認証コード送信（flow=email_change, Bearer 必須）
  const onVerify = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('ログインが必要です。再度ログインしてください。');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/user/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ flow: 'email_change', email: getValues('email') }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'verify failed');
      }
      setIsVerified(true);
    } catch (err) {
      console.error('verify failed:', err);
      alert('メール送信に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };

  const onReset = () => {
    setIsVerified(false);
    reset({ oldEmail, email: '', code: '' });
  };

  const onResendCode = () => {
    setIsVerified(false);
    onVerify();
  };

  // 変更確定（/v1/user/email/confirm, Bearer 必須）
  const onConfirm = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('ログインが必要です。再度ログインしてください。');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/user/email/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          flow: 'email_change',
          email: getValues('email'),
          code: getValues('code'),
        }),
      });
      if (!res.ok) {
        // JSONでもテキストでも受けられるようにする
        let rawMessage = '';
        try {
          const data = await res.json();
          rawMessage = data?.error || data?.message || '';
        } catch {
          rawMessage = (await res.text()) || '';
        }

        // ユーザー向けに和文へ整形
        const toUserMessage = (msg: string, status: number) => {
          const m = (msg || '').toLowerCase();
          // if (m.includes('expired')) return '認証コードの有効期限が切れています。新しいコードを再送してください。';
          // if (m.includes('invalid')) return '認証コードが正しくありません。もう一度入力してください。';
          if (m.includes('expired') || m.includes('invalid')) {
            return '認証コードが無効か、期限切れです。入力内容を確認して、もう一度お試しください。';
          }
          if (status === 429) return '試行回数の上限に達しました。しばらくしてから再度お試しください。';
          if (status >= 500) return 'サーバーでエラーが発生しました。時間をおいてもう一度お試しください。';
          return '認証に失敗しました。入力内容をご確認のうえ、もう一度お試しください。';
        };

        alert(toUserMessage(rawMessage, res.status));
        return;
      }
      // 正常終了
      onComplete(); // 例：マイページへ戻る/再取得など
    } catch (err) {
      console.error('confirm failed:', err);
      alert('メール変更に失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form>
      <p className="m-form-title">BLUE SKY LIFE メールアドレス変更</p>
      <div className="flex flex-col gap-8 mt-8 xmd:w-[64rem]">
        {/* 変更前メール（自動入力、編集不可） */}
        <InputField
          label="変更前のメールアドレス"
          type="email"
          register={register('oldEmail')}
          errors={errors}
          disabled
          readOnly
          className="pointer-events-none opacity-80"
        />
        {/* 変更後メール（入力可能） */}
        <InputField
          label="変更後のメールアドレス"
          type="email"
          register={register('email')}
          errors={errors}
        />

        <div className="flex justify-center mt-4">
          {!isVerified ? (
            <button
              type="button"
              className="m-btn outlined btn-forest"
              disabled={!canSendChange || isLoading}
              onClick={onVerify}
            >
              <em className="m-icon-login -mt-1 mr-2 text-[2.4rem]"></em>
              <span>認証コードを送る</span>
            </button>
          ) : (
            <button type="button" className="m-btn outlined" onClick={onReset} disabled={isLoading}>
              メールアドレス入力からやり直す
            </button>
          )}
        </div>

        {!isVerified && isLoading && (
          <div className="flex justify-center">
            <ClipLoader color="#000" loading={isLoading} />
          </div>
        )}

        {isVerified && (
          <>
            <div
              className="text-center bg-yellow-100 mt-6 py-8 px-2 rounded-lg border border-yellow-300"
              ref={refVerifyContainer}
            >
              <p>
                <strong>{watch('email')}</strong> 宛に認証コードを送信しました。
                <br />
                メールに記載の認証コードをご入力ください。
              </p>
            </div>

            <div className="flex flex-col gap-8">
              <div className="mx-auto">
                {/* <PinCodeField
                  label="認証コード"
                  register={register('code')}
                  errors={errors}
                  length={6}
                  onChange={(value) => {
                    setValue('code', value);
                    trigger('code');
                  }}
                  value={watch('code')}
                /> */}
              <InputField
                label="認証コード"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                {...register('code')}
                value={watch('code') || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                  setValue('code', v, { shouldValidate: true, shouldDirty: true });
                  // trigger('code'); // ここで即時バリデしたいなら有効化
                }}
              />
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="m-btn fill"
                  onClick={onConfirm}
                  disabled={!isValid || isLoading}
                >
                  変更を確定
                </button>
              </div>
              {isLoading && (
                <div className="flex justify-center">
                  <ClipLoader color="#000" loading={isLoading} />
                </div>
              )}
            </div>

            <div className="text-center bg-gray-100 py-8 px-2 rounded-lg border border-gray-300">
              <p>
                ※認証コードの再送信は、下のボタンを押してください。
                <br />
                {countdownTimer > 0 && `(再送可能まであと${countdownTimer}秒)`}
              </p>
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  className="m-btn btn-small outlined"
                  onClick={onResendCode}
                  disabled={countdownTimer > 0 || isLoading}
                >
                  認証コードを再送信
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default LoginForm;
